import { 
  Calendar, 
  Music, 
  Video, 
  Star, 
  Headphones, 
  Users,
  Shield,
  Zap,
  BarChart3,
  Clock,
  Camera,
  Mic,
  Settings,
  TrendingUp,
  Globe,
  Lock,
  Database,
  CheckCircle,
  FileText,
  DollarSign,
  Building,
  Smartphone,
  Activity,
  Cpu,
  Wifi
} from 'lucide-react';
import { ProductConfigs } from './types';

export const entertainmentSolutionsConfigs: ProductConfigs = {
  'event-production-platform': {
    keyBenefits: [
      {
        icon: Calendar,
        title: 'Comprehensive Event Planning',
        description: 'Advanced timeline management with automated crew scheduling, equipment tracking, and vendor coordination for seamless event production.',
        metrics: '95% faster event setup coordination',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Real-time Production Coordination',
        description: 'Live event coordination with instant communication tools, crew management, and real-time status updates across all production teams.',
        metrics: '40% reduction in coordination errors',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Budget & Resource Optimisation',
        description: 'Intelligent budget tracking with cost control alerts, resource allocation optimisation, and detailed financial reporting for maximum profitability.',
        metrics: '£25,000 average cost savings per event',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Shield,
        title: 'Safety Management System',
        description: 'Comprehensive safety protocols with incident tracking, risk assessment tools, and automated compliance reporting for secure event operations.',
        metrics: '98% safety compliance rate',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: Settings,
        title: 'Vendor Integration Hub',
        description: 'Centralised vendor management with contract tracking, performance monitoring, and automated payment processing for streamlined partnerships.',
        metrics: '60% faster vendor coordination',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Live Coordination Dashboard',
        description: 'Real-time event monitoring with instant alerts, team communication, and dynamic resource reallocation for flawless execution.',
        metrics: '85% improvement in event execution',
        color: 'from-cyan-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Event Analytics',
        description: 'Machine learning algorithms analyse past events to optimise crew allocation, predict equipment needs, and enhance audience experience.',
        metrics: '85% improvement in resource planning',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Multi-Venue Coordination',
        description: 'Centralised management of multiple venues with synchronized scheduling, shared resource pools, and unified reporting across all locations.',
        metrics: 'Manage 50+ venues simultaneously',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Analytics & ROI',
        description: 'Advanced analytics dashboard with audience engagement metrics, revenue optimisation insights, and predictive performance modelling.',
        metrics: '30% increase in event ROI',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: FileText,
        title: 'Compliance Management Suite',
        description: 'Automated compliance tracking with regulatory reporting, permit management, and audit trail documentation for seamless operations.',
        metrics: '100% compliance documentation',
        color: 'from-slate-500 to-gray-600'
      },
      {
        icon: Camera,
        title: 'Risk Assessment Platform',
        description: 'Predictive risk analysis with scenario planning, contingency management, and automated risk mitigation strategies for event security.',
        metrics: '70% reduction in event risks',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Building,
        title: 'Multi-Venue Scaling Engine',
        description: 'Intelligent scaling across multiple locations with resource sharing, synchronized operations, and centralized management capabilities.',
        metrics: 'Scale to 100+ concurrent venues',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2 Type II, ISO 27001, and GDPR standards ensuring regulatory adherence and data protection.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time security monitoring, vulnerability assessment, and automated security posture optimization.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting millions of concurrent users with auto-scaling, load balancing, and distributed architecture.',
        metrics: '10M+ concurrent user capacity',
        color: 'from-purple-600 to-violet-700'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent traffic filtering, real-time attack prevention, and automated response systems.',
        metrics: '99.99% uptime during attacks',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based authentication for maximum security.',
        metrics: '99.9% authentication security',
        color: 'from-green-600 to-emerald-700'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response for comprehensive security monitoring.',
        metrics: '<1 minute threat response time',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring, encrypted backups, and point-in-time recovery capabilities.',
        metrics: '99.99% data recovery success',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with zero-trust architecture, network anomaly detection, and intelligent traffic analysis.',
        metrics: '100% network visibility',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with compliance validation, security testing, and continuous risk assessment for proactive protection.',
        metrics: '24/7 security monitoring',
        color: 'from-emerald-600 to-teal-700'
      }
    ]
  },

  'music-production-suite': {
    keyBenefits: [
      {
        icon: Music,
        title: 'Professional Recording Interface',
        description: 'Advanced multi-track recording with real-time collaboration, version control, and seamless integration with industry-standard DAWs.',
        metrics: '10x faster collaboration workflows',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Rights & Royalty Management',
        description: 'Automated royalty calculation with blockchain verification, rights tracking, and transparent revenue distribution for all stakeholders.',
        metrics: '99.9% royalty calculation accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Global Distribution Network',
        description: 'One-click distribution to 200+ platforms with automated metadata management, regional licensing, and performance tracking.',
        metrics: 'Reach 200+ global platforms instantly',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Headphones,
        title: 'Client Management System',
        description: 'Comprehensive client portal with project tracking, approval workflows, and automated communication for streamlined client relations.',
        metrics: '50% improvement in client satisfaction',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Calendar,
        title: 'Equipment Booking Platform',
        description: 'Smart studio equipment scheduling with availability tracking, maintenance alerts, and resource optimization for maximum utilization.',
        metrics: '80% increase in equipment utilization',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: DollarSign,
        title: 'Financial Tracking Dashboard',
        description: 'Advanced project budgeting with expense tracking, revenue forecasting, and automated invoicing for complete financial control.',
        metrics: '90% improvement in project profitability',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Enhanced Audio Processing',
        description: 'Machine learning-powered audio enhancement, automatic mastering, and intelligent noise reduction for professional-quality results.',
        metrics: '90% reduction in post-production time',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Analytics & Insights',
        description: 'Comprehensive streaming analytics, audience demographics, and revenue forecasting with AI-powered trend prediction.',
        metrics: '40% improvement in release timing',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Workflow Automation',
        description: 'Intelligent automation for repetitive tasks, smart file organisation, and automated backup with version history management.',
        metrics: '75% reduction in manual tasks',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Wifi,
        title: 'Cloud Collaboration Hub',
        description: 'Real-time collaborative editing with cloud synchronization, remote recording capabilities, and cross-platform compatibility.',
        metrics: '100% remote collaboration support',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: FileText,
        title: 'Publishing Integration Suite',
        description: 'Direct integration with major music publishers, automated copyright registration, and streamlined licensing management.',
        metrics: '95% faster publishing workflows',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Studio Access',
        description: 'Full-featured mobile application with remote monitoring, project management, and on-the-go editing capabilities.',
        metrics: '24/7 mobile studio access',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2 Type II, ISO 27001, and GDPR standards ensuring regulatory adherence and data protection.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time security monitoring, vulnerability assessment, and automated security posture optimization.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting millions of concurrent users with auto-scaling, load balancing, and distributed architecture.',
        metrics: '10M+ concurrent user capacity',
        color: 'from-purple-600 to-violet-700'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent traffic filtering, real-time attack prevention, and automated response systems.',
        metrics: '99.99% uptime during attacks',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based authentication for maximum security.',
        metrics: '99.9% authentication security',
        color: 'from-green-600 to-emerald-700'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response for comprehensive security monitoring.',
        metrics: '<1 minute threat response time',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring, encrypted backups, and point-in-time recovery capabilities.',
        metrics: '99.99% data recovery success',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with zero-trust architecture, network anomaly detection, and intelligent traffic analysis.',
        metrics: '100% network visibility',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with compliance validation, security testing, and continuous risk assessment for proactive protection.',
        metrics: '24/7 security monitoring',
        color: 'from-emerald-600 to-teal-700'
      }
    ]
  },

  'video-production-platform': {
    keyBenefits: [
      {
        icon: Video,
        title: 'Complete Production Workflow',
        description: 'End-to-end video production management from pre-production planning through final delivery with integrated asset management.',
        metrics: '60% faster project completion',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Collaborative Review System',
        description: 'Real-time collaboration with frame-accurate feedback, version control, and automated approval workflows for streamlined production.',
        metrics: '80% faster review cycles',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Settings,
        title: 'Digital Asset Management',
        description: 'AI-powered asset organisation with smart tagging, automated transcoding, and intelligent search across unlimited media libraries.',
        metrics: 'Organise 1M+ assets instantly',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Headphones,
        title: 'Client Communication Hub',
        description: 'Integrated client portal with project updates, approval workflows, and real-time collaboration tools for enhanced client engagement.',
        metrics: '70% improvement in client satisfaction',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Budget Tracking System',
        description: 'Comprehensive budget management with expense tracking, cost forecasting, and automated financial reporting for project control.',
        metrics: '85% improvement in budget accuracy',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Clock,
        title: 'Delivery Management Platform',
        description: 'Automated delivery scheduling with format optimization, quality control, and multi-platform distribution for seamless content delivery.',
        metrics: '95% on-time delivery rate',
        color: 'from-emerald-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Post-Production',
        description: 'Machine learning algorithms for automated editing, colour correction, and audio synchronisation with professional-grade results.',
        metrics: '70% reduction in editing time',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Multi-Platform Distribution',
        description: 'Automated distribution to streaming platforms, social media, and broadcast networks with format optimisation and delivery tracking.',
        metrics: 'Deploy to 50+ platforms simultaneously',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Analytics Suite',
        description: 'Comprehensive viewership analytics, engagement metrics, and ROI tracking with predictive audience behaviour modelling.',
        metrics: '45% improvement in content performance',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Cpu,
        title: 'Cloud Rendering Engine',
        description: 'High-performance cloud rendering with GPU acceleration, distributed processing, and real-time preview capabilities for faster production.',
        metrics: '10x faster rendering speeds',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Editing Suite',
        description: 'Professional mobile editing tools with cloud synchronization, remote collaboration, and on-the-go content creation capabilities.',
        metrics: '100% mobile workflow support',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Activity,
        title: 'Automated Workflow Engine',
        description: 'Intelligent workflow automation with task scheduling, resource allocation, and automated quality control for streamlined production.',
        metrics: '80% workflow automation rate',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2 Type II, ISO 27001, and GDPR standards ensuring regulatory adherence and data protection.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time security monitoring, vulnerability assessment, and automated security posture optimization.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting millions of concurrent users with auto-scaling, load balancing, and distributed architecture.',
        metrics: '10M+ concurrent user capacity',
        color: 'from-purple-600 to-violet-700'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent traffic filtering, real-time attack prevention, and automated response systems.',
        metrics: '99.99% uptime during attacks',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based authentication for maximum security.',
        metrics: '99.9% authentication security',
        color: 'from-green-600 to-emerald-700'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response for comprehensive security monitoring.',
        metrics: '<1 minute threat response time',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring, encrypted backups, and point-in-time recovery capabilities.',
        metrics: '99.99% data recovery success',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with zero-trust architecture, network anomaly detection, and intelligent traffic analysis.',
        metrics: '100% network visibility',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with compliance validation, security testing, and continuous risk assessment for proactive protection.',
        metrics: '24/7 security monitoring',
        color: 'from-emerald-600 to-teal-700'
      }
    ]
  },

  'talent-management-system': {
    keyBenefits: [
      {
        icon: Star,
        title: 'Comprehensive Talent Profiles',
        description: 'Advanced portfolio management with digital portfolios, skill tracking, career milestone recording, and automated profile optimisation.',
        metrics: '50% increase in booking opportunities',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Calendar,
        title: 'Smart Booking Coordination',
        description: 'AI-powered scheduling with conflict detection, availability optimisation, and automated booking confirmations across multiple venues.',
        metrics: '90% reduction in scheduling conflicts',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Revenue & Career Analytics',
        description: 'Comprehensive earnings tracking with career progression analytics, market rate comparisons, and revenue optimisation insights.',
        metrics: '35% increase in talent earnings',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Audition Management Platform',
        description: 'Streamlined audition scheduling with video submissions, callback management, and automated talent matching for casting directors.',
        metrics: '75% faster casting processes',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Marketing & Promotion Tools',
        description: 'Integrated marketing suite with social media management, promotional campaigns, and brand building tools for talent visibility.',
        metrics: '120% increase in talent visibility',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Activity,
        title: 'Performance Tracking System',
        description: 'Detailed performance analytics with audience feedback, engagement metrics, and career milestone tracking for continuous improvement.',
        metrics: '60% improvement in performance quality',
        color: 'from-emerald-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Talent Matching',
        description: 'Machine learning algorithms match talent with opportunities based on skills, availability, performance history, and market demand.',
        metrics: '85% successful match rate',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Market Intelligence Platform',
        description: 'Real-time industry insights with trend analysis, rate benchmarking, and opportunity forecasting for strategic career planning.',
        metrics: '40% improvement in career decisions',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Global Talent Network',
        description: 'Connect with international opportunities, manage multi-territory contracts, and coordinate global talent exchanges seamlessly.',
        metrics: 'Access 500+ global opportunities',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: FileText,
        title: 'Contract Automation Suite',
        description: 'Intelligent contract generation with legal compliance, automated negotiations, and digital signature integration for seamless deals.',
        metrics: '90% faster contract processing',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Performance Analytics Engine',
        description: 'Advanced performance tracking with audience engagement analysis, career trajectory modeling, and success prediction algorithms.',
        metrics: '70% improvement in career planning',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile App Access',
        description: 'Full-featured mobile application with booking management, portfolio updates, and real-time communication for on-the-go talent management.',
        metrics: '24/7 mobile talent management',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2 Type II, ISO 27001, and GDPR standards ensuring regulatory adherence and data protection.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time security monitoring, vulnerability assessment, and automated security posture optimization.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting millions of concurrent users with auto-scaling, load balancing, and distributed architecture.',
        metrics: '10M+ concurrent user capacity',
        color: 'from-purple-600 to-violet-700'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent traffic filtering, real-time attack prevention, and automated response systems.',
        metrics: '99.99% uptime during attacks',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based authentication for maximum security.',
        metrics: '99.9% authentication security',
        color: 'from-green-600 to-emerald-700'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response for comprehensive security monitoring.',
        metrics: '<1 minute threat response time',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring, encrypted backups, and point-in-time recovery capabilities.',
        metrics: '99.99% data recovery success',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with zero-trust architecture, network anomaly detection, and intelligent traffic analysis.',
        metrics: '100% network visibility',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with compliance validation, security testing, and continuous risk assessment for proactive protection.',
        metrics: '24/7 security monitoring',
        color: 'from-emerald-600 to-teal-700'
      }
    ]
  },

  'streaming-media-platform': {
    keyBenefits: [
      {
        icon: Headphones,
        title: 'Advanced Content Management',
        description: 'Sophisticated content organisation with AI-powered categorisation, automated metadata generation, and intelligent content recommendations.',
        metrics: '300% improvement in content discovery',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Platform Distribution',
        description: 'Simultaneous distribution across web, mobile, smart TV, and emerging platforms with adaptive streaming and format optimisation.',
        metrics: 'Reach 15+ platform types instantly',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Audience Analytics',
        description: 'Deep audience insights with viewing behaviour analysis, demographic segmentation, and predictive engagement modelling.',
        metrics: '60% increase in viewer retention',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Video,
        title: 'Live Streaming Platform',
        description: 'Professional live streaming with multi-camera support, real-time switching, and interactive audience engagement features.',
        metrics: '99.9% live stream reliability',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Community Building Tools',
        description: 'Integrated social features with fan clubs, discussion forums, and creator-audience interaction platforms for engaged communities.',
        metrics: '250% increase in community engagement',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: DollarSign,
        title: 'Revenue Optimization Engine',
        description: 'Advanced monetization strategies with subscription management, ad optimization, and creator economy tools for maximum revenue.',
        metrics: '180% increase in creator earnings',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Recommendations',
        description: 'Machine learning recommendation engine with personalised content curation, trend prediction, and automated playlist generation.',
        metrics: '75% improvement in user engagement',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Monetisation Optimisation',
        description: 'Dynamic pricing algorithms with subscription management, ad revenue optimisation, and multi-tier monetisation strategies.',
        metrics: '45% increase in average revenue per user',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Community Engagement Tools',
        description: 'Interactive features including live chat, social sharing, community forums, and creator-audience engagement platforms.',
        metrics: '200% increase in community interaction',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Cpu,
        title: 'Edge Computing Platform',
        description: 'Global edge computing network with intelligent content delivery, regional optimization, and real-time performance scaling.',
        metrics: '70% reduction in global latency',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Optimization Suite',
        description: 'Advanced mobile streaming with offline downloads, adaptive quality, and mobile-first user interface optimization.',
        metrics: '95% mobile performance optimization',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Creator Economy Tools',
        description: 'Comprehensive creator support with analytics dashboards, monetization tools, and audience growth strategies for content creators.',
        metrics: '300% creator productivity increase',
        color: 'from-amber-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2 Type II, ISO 27001, and GDPR standards ensuring regulatory adherence and data protection.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time security monitoring, vulnerability assessment, and automated security posture optimization.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting millions of concurrent users with auto-scaling, load balancing, and distributed architecture.',
        metrics: '10M+ concurrent user capacity',
        color: 'from-purple-600 to-violet-700'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent traffic filtering, real-time attack prevention, and automated response systems.',
        metrics: '99.99% uptime during attacks',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based authentication for maximum security.',
        metrics: '99.9% authentication security',
        color: 'from-green-600 to-emerald-700'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response for comprehensive security monitoring.',
        metrics: '<1 minute threat response time',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring, encrypted backups, and point-in-time recovery capabilities.',
        metrics: '99.99% data recovery success',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with zero-trust architecture, network anomaly detection, and intelligent traffic analysis.',
        metrics: '100% network visibility',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with compliance validation, security testing, and continuous risk assessment for proactive protection.',
        metrics: '24/7 security monitoring',
        color: 'from-emerald-600 to-teal-700'
      }
    ]
  },

  'venue-management-system': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Complete Venue Operations',
        description: 'Comprehensive venue management with booking coordination, event planning, facilities management, and customer experience optimisation.',
        metrics: '80% increase in venue utilisation',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Calendar,
        title: 'Advanced Event Coordination',
        description: 'Sophisticated event planning with resource allocation, timeline management, and real-time coordination across all venue operations.',
        metrics: '95% event success rate',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Customer Experience Analytics',
        description: 'Detailed customer journey tracking with satisfaction metrics, feedback analysis, and experience optimisation recommendations.',
        metrics: '40% improvement in customer satisfaction',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Mic,
        title: 'Food & Beverage Management',
        description: 'Complete F&B operations with inventory management, menu planning, and automated ordering systems for seamless service delivery.',
        metrics: '65% improvement in F&B efficiency',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Shield,
        title: 'Security Management Platform',
        description: 'Integrated security systems with access control, surveillance monitoring, and incident management for comprehensive venue safety.',
        metrics: '99% security incident prevention',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: DollarSign,
        title: 'Financial Reporting Suite',
        description: 'Advanced financial analytics with revenue tracking, cost analysis, and profitability optimization for data-driven decisions.',
        metrics: '50% improvement in venue profitability',
        color: 'from-cyan-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Space Optimisation',
        description: 'Machine learning algorithms optimise space utilisation, predict demand patterns, and automate resource allocation for maximum efficiency.',
        metrics: '65% improvement in space efficiency',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: TrendingUp,
        title: 'Revenue Optimisation Engine',
        description: 'Dynamic pricing algorithms with demand forecasting, competitive analysis, and automated revenue management strategies.',
        metrics: '50% increase in revenue per event',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Integrated Operations Management',
        description: 'Unified platform connecting all venue systems with workflow automation, staff coordination, and real-time performance monitoring.',
        metrics: '75% operational efficiency increase',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Building,
        title: 'Smart Building Integration',
        description: 'IoT connectivity with environmental controls, energy management, and automated facility systems for intelligent venue operations.',
        metrics: '40% reduction in operational costs',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Activity,
        title: 'Sustainability Tracking System',
        description: 'Environmental impact monitoring with carbon footprint tracking, waste management, and sustainability reporting for eco-friendly operations.',
        metrics: '60% improvement in sustainability metrics',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Management Platform',
        description: 'Complete mobile venue management with staff coordination, guest services, and real-time operational control from any device.',
        metrics: '100% mobile operational control',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2 Type II, ISO 27001, and GDPR standards ensuring regulatory adherence and data protection.',
        metrics: '100% regulatory compliance assurance',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time security monitoring, vulnerability assessment, and automated security posture optimization.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting millions of concurrent users with auto-scaling, load balancing, and distributed architecture.',
        metrics: '10M+ concurrent user capacity',
        color: 'from-purple-600 to-violet-700'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent traffic filtering, real-time attack prevention, and automated response systems.',
        metrics: '99.99% uptime during attacks',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO integration with biometric authentication, hardware tokens, and risk-based authentication for maximum security.',
        metrics: '99.9% authentication security',
        color: 'from-green-600 to-emerald-700'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response for comprehensive security monitoring.',
        metrics: '<1 minute threat response time',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with real-time DLP monitoring, encrypted backups, and point-in-time recovery capabilities.',
        metrics: '99.99% data recovery success',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with zero-trust architecture, network anomaly detection, and intelligent traffic analysis.',
        metrics: '100% network visibility',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with compliance validation, security testing, and continuous risk assessment for proactive protection.',
        metrics: '24/7 security monitoring',
        color: 'from-emerald-600 to-teal-700'
      }
    ]
  }
};